import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Camera, Trash2, Star, Loader2, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';

const MAX_PHOTOS = 10;

export const ProfilePhotos = ({ onUserUpdate }) => {
  const [photos, setPhotos] = useState([]);
  const [blobUrls, setBlobUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [dragIndex, setDragIndex] = useState(null);

  const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  const loadBlob = useCallback(async (photo) => {
    try {
      const res = await axios.get(`/photos/${photo.id}/file`, {
        headers: authHeader(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      setBlobUrls((prev) => ({ ...prev, [photo.id]: url }));
    } catch (e) {
      // ignore individual image load failures
    }
  }, []);

  const fetchPhotos = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/photos', { headers: authHeader() });
      const list = res.data || [];
      setPhotos(list);
      list.forEach((p) => loadBlob(p));
    } catch (e) {
      toast.error('Could not load your photos');
    } finally {
      setLoading(false);
    }
  }, [loadBlob]);

  const blobUrlsRef = useRef({});
  useEffect(() => {
    blobUrlsRef.current = blobUrls;
  }, [blobUrls]);

  useEffect(() => {
    fetchPhotos();
  }, [fetchPhotos]);

  useEffect(() => {
    return () => {
      Object.values(blobUrlsRef.current).forEach((u) => URL.revokeObjectURL(u));
    };
  }, []);

  const handleFilePick = () => fileInputRef.current?.click();

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/jpg'].includes(file.type)) {
      toast.error('Only JPG and PNG images are allowed');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      return;
    }
    if (photos.length >= MAX_PHOTOS) {
      toast.error(`You can upload up to ${MAX_PHOTOS} photos`);
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('caption', '');
      await axios.post('/photos/upload', formData, {
        headers: { ...authHeader(), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Photo added');
      await fetchPhotos();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const setAsProfile = async (photo) => {
    try {
      await axios.put(`/photos/${photo.id}`, { is_profile_picture: true }, { headers: authHeader() });
      toast.success('Profile picture updated');
      await fetchPhotos();
      try {
        const me = await axios.get('/users/me', { headers: authHeader() });
        if (onUserUpdate) onUserUpdate(me.data);
      } catch (e) { /* avatar refresh is best-effort */ }
    } catch (err) {
      toast.error('Could not set profile picture');
    }
  };

  const deletePhoto = async (photo) => {
    if (!window.confirm('Remove this photo? You can restore it later from the recycle bin.')) return;
    try {
      await axios.delete(`/photos/${photo.id}`, { headers: authHeader() });
      toast.success('Photo removed');
      setBlobUrls((prev) => {
        const next = { ...prev };
        if (next[photo.id]) URL.revokeObjectURL(next[photo.id]);
        delete next[photo.id];
        return next;
      });
      await fetchPhotos();
    } catch (err) {
      toast.error('Could not remove photo');
    }
  };

  const handleDrop = async (targetIdx) => {
    if (dragIndex === null || dragIndex === targetIdx) { setDragIndex(null); return; }
    const moved = photos[dragIndex];
    setDragIndex(null);
    try {
      await axios.post(`/photos/${moved.id}/reorder`, null, { params: { new_order: targetIdx }, headers: authHeader() });
      await fetchPhotos();
    } catch (e) {
      toast.error('Could not reorder photos');
    }
  };

  return (
    <div className="mb-8" data-testid="profile-photos-section">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-emerald-800 flex items-center space-x-2">
          <Camera className="w-5 h-5" />
          <span>My Photos</span>
        </h3>
        <span className="text-sm text-emerald-600">{photos.length}/{MAX_PHOTOS}</span>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png"
        className="hidden"
        onChange={handleUpload}
        data-testid="photo-upload-input"
      />

      {loading ? (
        <div className="flex items-center justify-center py-10 text-emerald-500">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
          {photos.map((photo, idx) => (
            <div
              key={photo.id}
              draggable
              onDragStart={() => setDragIndex(idx)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(idx)}
              className="relative group aspect-square rounded-xl overflow-hidden border-2 border-emerald-100 bg-emerald-50 cursor-move"
              data-testid={`photo-card-${photo.id}`}
            >
              {blobUrls[photo.id] ? (
                <img src={blobUrls[photo.id]} alt="profile" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Loader2 className="w-5 h-5 animate-spin text-emerald-400" />
                </div>
              )}

              {photo.is_profile_picture && (
                <span className="absolute top-2 left-2 bg-emerald-600 text-white text-xs px-2 py-0.5 rounded-full flex items-center space-x-1" data-testid={`photo-primary-badge-${photo.id}`}>
                  <Star className="w-3 h-3 fill-white" />
                  <span>Profile</span>
                </span>
              )}

              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center gap-2 p-2">
                {!photo.is_profile_picture && (
                  <button
                    onClick={() => setAsProfile(photo)}
                    title="Set as profile picture"
                    className="bg-white text-emerald-700 rounded-full p-2 hover:bg-emerald-50"
                    data-testid={`photo-set-primary-${photo.id}`}
                  >
                    <Star className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={() => deletePhoto(photo)}
                  title="Delete photo"
                  className="bg-white text-red-600 rounded-full p-2 hover:bg-red-50"
                  data-testid={`photo-delete-${photo.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {photos.length < MAX_PHOTOS && (
            <button
              onClick={handleFilePick}
              disabled={uploading}
              className="aspect-square rounded-xl border-2 border-dashed border-emerald-300 flex flex-col items-center justify-center text-emerald-500 hover:bg-emerald-50 hover:border-emerald-400 transition-colors disabled:opacity-60"
              data-testid="photo-add-btn"
            >
              {uploading ? (
                <Loader2 className="w-7 h-7 animate-spin" />
              ) : (
                <>
                  <ImagePlus className="w-7 h-7 mb-1" />
                  <span className="text-sm font-medium">Add Photo</span>
                </>
              )}
            </button>
          )}
        </div>
      )}

      {!loading && photos.length === 0 && (
        <p className="text-sm text-emerald-600 mt-3">
          Add up to {MAX_PHOTOS} photos. Tap a photo to set it as your profile picture.
        </p>
      )}
    </div>
  );
};

export default ProfilePhotos;
